import { FacetValueCount, FacetValues, ReplayAPI_StringFacetValue, StringFacetValue } from '../../../types/Facet';
import { Field } from '../../../types/Field';
import StringFacet from './StringFacetPresentation';
import { FacetLogic, RenderFacetProps } from './Types';

// facetSelection is an array of strings.
function createFilter(facetSelection: FacetValues<StringFacetValue>, field: Field) {
    const filterClauses = facetSelection.facetValues.map((facetValue: StringFacetValue) => field.systemSettings.filterClause.replace('{{VALUE}}', facetValue.value).replace('{{OPERATOR}}', 'eq'));
    if (filterClauses.length === 0) {
        return "";
    }

    const filterClausesString = filterClauses.join(' and ');    
    return `(${filterClausesString})`;
}

function createRequest(field: Field) {
    // We alway set count to 0, so we retrieve all the values. This is required to guarantee accuracy in the count,
    // see https://learn.microsoft.com/en-us/azure/search/search-faceted-navigation?tabs=portal-facet%2Cportal-facet-response#discrepancies-in-facet-counts
    return `${field.name}, count:0`;
}

function extractFacetValues(facetName: string, facetValuesFromSearch: ReplayAPI_StringFacetValue[]) : FacetValueCount<StringFacetValue>[] {    
    let extractedValues: FacetValueCount<StringFacetValue>[] = [];    

    facetValuesFromSearch = facetValuesFromSearch.sort((a, b) => a.value.localeCompare(b.value));

    facetValuesFromSearch.forEach((facetValue: ReplayAPI_StringFacetValue) => {
        // Filter out empty values.
        if (!facetValue.value) {
            return;
        }
        
        extractedValues.push({            
            facetValue: new StringFacetValue(facetName, facetValue.value),
            count: facetValue.count
        })
    });    
    
    return extractedValues;
}

function renderFacet (props: RenderFacetProps) {  
    return <StringFacet
             key={props.field.name} 
             name={props.field.name}
             toggleDisplayMode={props.toggleDisplayMode}
             isExecutingSearch={props.isExecutingSearch}
             displayMode={props.displayMode}
             displayName={props.field.userSettings.displayName}
             facetValues={props.facetValues}
             facetSelection={props.facetSelection}
             onChangeFacet={props.onChangeFacetSelection}/>
}

// The facetValue contains the value of the entry that has been clicked.
// If this entry currently exists in the selection it will be removed else it will be added.
function createUpdatedSelection(facetValue: StringFacetValue, prevFacetSelection: FacetValues<StringFacetValue>): FacetValues<StringFacetValue> {        
    const isValueCurrentlySelected = prevFacetSelection.facetValues.some((item: StringFacetValue) => item.value === facetValue.value);

    const newSelectionsOfFacetValues = isValueCurrentlySelected ?
        prevFacetSelection.facetValues.filter((item: StringFacetValue) => item.value !== facetValue.value) : // Remove item
        [...prevFacetSelection.facetValues, facetValue]; // Add item

    return {
        facetName: prevFacetSelection.facetName,
        facetValues: newSelectionsOfFacetValues,
    };
}

export const stringFacetLogic: FacetLogic = {
    dataType: 'Edm.String',
    createFilter: createFilter,
    createRequest: createRequest,
    extractFacetValues: extractFacetValues,
    renderFacet: renderFacet,
    createUpdatedSelection: createUpdatedSelection,
}

export const stringArrayFacetLogic: FacetLogic = {
    dataType: 'Collection(Edm.String)',
    createFilter: createFilter,
    createRequest: createRequest,
    extractFacetValues: extractFacetValues,
    renderFacet: renderFacet,
    createUpdatedSelection: createUpdatedSelection,
}